// Utilitaires d'URLs SEO pour la CVthèque "Disponibles".
// Format cible :
//   /disponibles/<qualification>             ex. /disponibles/bnssa
//   /disponibles/<qualification>/<region>    ex. /disponibles/bnssa/bretagne
//
// Ces pages sont indexables : elles pré-appliquent un filtre qualification (+ région)
// sur la liste des profils disponibles, avec un <title>/meta/canonical dédiés.

import {
  CANDIDATE_QUALIFICATIONS,
  FRENCH_REGIONS,
  type CandidateQualification,
} from '@/hooks/useAvailableCandidates'

/** Slugifie une chaîne pour usage en URL (sans accents, minuscules, tirets). */
export function slugify(input: string): string {
  if (!input) return ''
  return input
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // accents
    .replace(/['’`"]/g, ' ')
    .replace(/&/g, ' et ')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
}

/** Slug d'une qualification : "BPJEPS AAN" -> "bpjeps-aan", "BNSSA" -> "bnssa". */
export function qualificationToSlug(qualification: CandidateQualification): string {
  return slugify(qualification)
}

/** Slug d'une région : "Île-de-France" -> "ile-de-france". */
export function regionToSlug(region: string): string {
  return slugify(region)
}

/** Retrouve la qualification exacte depuis un slug d'URL (insensible à la casse/accents). */
export function slugToQualification(
  slug: string | undefined
): CandidateQualification | null {
  if (!slug) return null
  const s = slugify(slug)
  return (
    CANDIDATE_QUALIFICATIONS.find((q) => qualificationToSlug(q) === s) ?? null
  )
}

/** Retrouve la région exacte (avec accents) depuis un slug d'URL. */
export function slugToRegion(slug: string | undefined): string | null {
  if (!slug) return null
  const s = slugify(slug)
  return FRENCH_REGIONS.find((r) => regionToSlug(r) === s) ?? null
}

/** Construit le path d'une page CVthèque filtrée (qualification + région optionnelle). */
export function buildCvthequePath(
  qualification: CandidateQualification,
  region?: string | null
): string {
  const base = `/disponibles/${qualificationToSlug(qualification)}`
  return region ? `${base}/${regionToSlug(region)}` : base
}

/**
 * Génère toutes les URLs SEO indexables de la CVthèque :
 * - une page par qualification,
 * - une page par couple (qualification, région).
 * Utilisé pour alimenter le sitemap.
 */
export function buildAllCvthequePaths(): string[] {
  const paths: string[] = []
  for (const q of CANDIDATE_QUALIFICATIONS) {
    paths.push(buildCvthequePath(q))
    for (const r of FRENCH_REGIONS) {
      paths.push(buildCvthequePath(q, r))
    }
  }
  return paths
}
